/**
 * useAssignment Hook
 * Manages comp assignment operations and assignment history
 */

import { useState, useCallback, useRef } from 'react';
import compsAPI from '../lib/services/compsAPI';
import { 
  validateCompAssignmentInput, 
  validateBulkAssignmentInput,
  validateCompUpdateInput,
  validateCompIdInput
} from '../lib/utils/compValidators';
import { formatErrorMessage } from '../lib/utils/compFormatters';

/**
 * Custom hook for managing comp assignment operations
 * @param {string} filmId - IMDB ID of the film (optional, can be set per operation)
 * @returns {Object} - Hook state and methods
 */
export const useAssignment = (filmId = null) => {
  // State management
  const [assignmentState, setAssignmentState] = useState({
    loading: false,
    error: null,
    lastOperation: null,
    lastOperationTime: null
  });


  const [assignmentHistory, setAssignmentHistory] = useState({
    data: [],
    loading: false,
    error: null,
    lastFetched: null
  });

  // Refs for managing async operations
  const mountedRef = useRef(true);

  /**
   * Set assignment state
   * @param {Object} updates - State updates
   */
  const setAssignmentStateUpdate = useCallback((updates) => {
    if (mountedRef.current) {
      setAssignmentState(prev => ({ ...prev, ...updates }));
    }
  }, []);


  /**
   * Set assignment history state
   * @param {Object} updates - State updates
   */
  const setAssignmentHistoryUpdate = useCallback((updates) => {
    if (mountedRef.current) {
      setAssignmentHistory(prev => ({ ...prev, ...updates }));
    }
  }, []);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    if (mountedRef.current) {
      setAssignmentStateUpdate({ error: null });
      setAssignmentHistoryUpdate({ error: null });
    }
  }, [setAssignmentStateUpdate, setAssignmentHistoryUpdate]);


  /**
   * Assign a single comp
   * @param {Object} assignment - Assignment data
   * @returns {Promise<Object>} - Assignment result
   */
  const assignComp = useCallback(async (assignment) => {
    // Validate assignment input
    const validation = validateCompAssignmentInput(assignment);
    if (!validation.isValid) {
      setAssignmentStateUpdate({
        loading: false,
        error: validation.error
      });
      throw new Error(validation.error);
    }

    // Set loading state
    setAssignmentStateUpdate({
      loading: true,
      error: null
    });

    try {
      const response = await compsAPI.assignComp(assignment);

      if (!mountedRef.current) return null;

      if (response.success) {
        setAssignmentStateUpdate({
          loading: false,
          error: null,
          lastOperation: 'assign',
          lastOperationTime: new Date().toISOString()
        });
        return response;
      } else {
        throw new Error('Failed to assign comp');
      }
    } catch (error) {
      if (!mountedRef.current) return null;

      // Log API errors for debugging
      console.error('❌ Assignment API Error:', error.message);

      const errorMessage = formatErrorMessage(error);
      setAssignmentStateUpdate({
        loading: false,
        error: errorMessage
      });
      throw new Error(errorMessage);
    }
  }, [setAssignmentStateUpdate]);

  /**
   * Bulk assign multiple comps
   * @param {Array} assignments - Array of assignment objects
   * @param {string} assignedBy - User ID of the assigner
   * @returns {Promise<Object>} - Bulk assignment result
   */
  const bulkAssign = useCallback(async (assignments, assignedBy) => {
    // Validate bulk assignment input
    const validation = validateBulkAssignmentInput(assignments, assignedBy);
    if (!validation.isValid) {
      setAssignmentStateUpdate({
        loading: false,
        error: validation.error
      });
      throw new Error(validation.error);
    }

    // Set loading state
    setAssignmentStateUpdate({
      loading: true,
      error: null
    });

    try {
      const response = await compsAPI.bulkAssign(validation.validAssignments, assignedBy);

      if (!mountedRef.current) return null;

      if (response.success) {
        setAssignmentStateUpdate({
          loading: false,
          error: null,
          lastOperation: 'bulk_assign',
          lastOperationTime: new Date().toISOString()
        });
        return response;
      } else {
        throw new Error('Failed to bulk assign comps');
      }
    } catch (error) {
      if (!mountedRef.current) return null;

      const errorMessage = formatErrorMessage(error);
      setAssignmentStateUpdate({
        loading: false,
        error: errorMessage
      });
      throw new Error(errorMessage);
    }
  }, [setAssignmentStateUpdate]);

  /**
   * Update an existing comp
   * @param {string} compId - ID of the comp to update
   * @param {Object} updates - Updates to apply
   * @returns {Promise<Object>} - Update result
   */
  const updateComp = useCallback(async (compId, updates) => {
    // Validate comp ID
    const compIdValidation = validateCompIdInput(compId);
    if (!compIdValidation.isValid) {
      setAssignmentStateUpdate({
        loading: false,
        error: compIdValidation.error
      });
      throw new Error(compIdValidation.error);
    }

    // Validate update input
    const updateValidation = validateCompUpdateInput(updates);
    if (!updateValidation.isValid) {
      setAssignmentStateUpdate({
        loading: false,
        error: updateValidation.error
      });
      throw new Error(updateValidation.error);
    }

    // Set loading state
    setAssignmentStateUpdate({
      loading: true,
      error: null
    });

    try {
      const response = await compsAPI.updateComp(compId, updates);

      if (!mountedRef.current) return null;

      if (response.success) {
        setAssignmentStateUpdate({
          loading: false,
          error: null,
          lastOperation: 'update',
          lastOperationTime: new Date().toISOString()
        });
        return response;
      } else {
        throw new Error('Failed to update comp');
      }
    } catch (error) {
      if (!mountedRef.current) return null;

      const errorMessage = formatErrorMessage(error);
      setAssignmentStateUpdate({
        loading: false,
        error: errorMessage
      });
      throw new Error(errorMessage);
    }
  }, [setAssignmentStateUpdate]);

  /**
   * Delete a comp
   * @param {string} compId - ID of the comp to delete
   * @returns {Promise<Object>} - Delete result
   */
  const deleteComp = useCallback(async (compId) => {
    // Validate comp ID
    const compIdValidation = validateCompIdInput(compId);
    if (!compIdValidation.isValid) {
      setAssignmentStateUpdate({
        loading: false,
        error: compIdValidation.error
      });
      throw new Error(compIdValidation.error);
    }

    // Set loading state
    setAssignmentStateUpdate({
      loading: true,
      error: null
    });

    try {
      const response = await compsAPI.deleteComp(compId);

      if (!mountedRef.current) return null;

      if (response.success) {
        setAssignmentStateUpdate({
          loading: false,
          error: null,
          lastOperation: 'delete',
          lastOperationTime: new Date().toISOString()
        });
        return response;
      } else {
        throw new Error('Failed to delete comp');
      }
    } catch (error) {
      if (!mountedRef.current) return null;

      const errorMessage = formatErrorMessage(error);
      setAssignmentStateUpdate({
        loading: false,
        error: errorMessage
      });
      throw new Error(errorMessage);
    }
  }, [setAssignmentStateUpdate]);

  /**
   * Fetch assignment history for a comp
   * @param {string} compId - ID of the comp
   * @returns {Promise<Array>} - Array of history entries
   */
  const fetchAssignmentHistory = useCallback(async (compId) => {
    // Validate comp ID
    const compIdValidation = validateCompIdInput(compId);
    if (!compIdValidation.isValid) {
      setAssignmentHistoryUpdate({
        loading: false,
        error: compIdValidation.error
      });
      return [];
    }

    // Set loading state
    setAssignmentHistoryUpdate({
      loading: true,
      error: null
    });

    try {
      const response = await compsAPI.getCompHistory(compId);

      if (!mountedRef.current) return [];

      if (response.success && response.data) {
        const historyData = response.data.history || [];
        setAssignmentHistoryUpdate({
          data: historyData,
          loading: false,
          error: null,
          lastFetched: new Date().toISOString()
        });
        return historyData;
      } else {
        setAssignmentHistoryUpdate({
          loading: false,
          error: 'Failed to fetch assignment history'
        });
        return [];
      }
    } catch (error) {
      if (!mountedRef.current) return [];

      const errorMessage = formatErrorMessage(error);
      setAssignmentHistoryUpdate({
        loading: false,
        error: errorMessage
      });
      return [];
    }
  }, [setAssignmentHistoryUpdate]);



  // Return hook interface
  return {
    // Assignment state
    loading: assignmentState.loading,
    error: assignmentState.error,
    lastOperation: assignmentState.lastOperation,
    lastOperationTime: assignmentState.lastOperationTime,
    
    // Assignment history state
    assignmentHistory: assignmentHistory.data,
    historyLoading: assignmentHistory.loading,
    historyError: assignmentHistory.error,
    historyLastFetched: assignmentHistory.lastFetched,
    
    // Methods
    assignComp,
    bulkAssign,
    updateComp,
    deleteComp,
    fetchAssignmentHistory,
    clearError,
    
    // Utilities
    isLoaded: !assignmentState.loading && !assignmentState.error,
    hasError: !!assignmentState.error,
    hasHistory: !assignmentHistory.loading && !assignmentHistory.error && assignmentHistory.data.length > 0
  };
};

export default useAssignment;
